// NATIVE 
import * as React from 'react';


// INSTALLED 
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import TableRow from '@mui/material/TableRow';
import EditIcon from '@mui/icons-material/Edit';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import FormGroup from '@mui/material/FormGroup';
import DeleteIcon from '@mui/icons-material/Delete';
import Menu, { MenuProps } from '@mui/material/Menu';
import { styled, alpha  } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import TableContainer from '@mui/material/TableContainer';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FormControlLabel from '@mui/material/FormControlLabel';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, Card, Chip, Pagination, TablePagination } from '@mui/material';



// CUSTOM 
import { paddingCardsTables, timeToClosedToast } from '../../commons';
import MultipleSnackbars from '../MultipleSnackbars';
import { showNumberRowsTable } from '../../commons/utils/values';
import { EditDialogCargo } from '../modals/cargos/EditDialogCargo';
import { ModalSessionExpired } from '../generics/ModalSessionExpired';
import { CreateDialogCargo } from '../modals/cargos/CreateDialogCargo';
import { DetailDialogCargo } from '../modals/cargos/DetailDialogCargo';
import { closeSessionGlobal, corregirTexto, firstLetterUpperCase, hasPemissions, usarFuncionesComposicion } from '../../commons/utils';
import { getCargosByPagination, getCargosStore, storeActiveCargo, storeDesactiveCargo, storeDetailCargos } from '../../store/cargos';
import { SnackAlert } from '../SnackAlert';







const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    color: theme.palette.secondary.contrastText,
    fontSize: 15,
    fontWeight: 700
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontWeight: 500,
    color: '#2e263de6'
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));








/**
 * TableCargos component displays a table of cargos with pagination, filtering, and various actions.
 * 
 * @component
 * 
 * @example
 * return (
 *   <TableCargos />
 * )
 * 
 * @returns {JSX.Element} The rendered TableCargos component.
 * 
 * @description
 * This component fetches and displays a list of cargos from the Redux store. It includes functionalities for:
 * - Viewing cargo details
 * - Editing cargos
 * - Deactivating cargos
 * - Restoring cargos
 * 
 * The component uses several React hooks:
 * - `useDispatch` to dispatch actions to the Redux store.
 * - `useSelector` to select the cargos list from the Redux store.
 * - `useState` to manage local state for various UI elements and data.
 * - `useEffect` to fetch data and check permissions on component mount.
 * 
 * The component also includes styled components for custom styling of the table and menu.
 * 
 * @hook
 * - `useDispatch`
 * - `useSelector`
 * - `useState`
 * - `useEffect`
 * 
 * @state {any} cargos_list - List of cargos from the Redux store.
 * @state {null | HTMLElement} anchorEl - Anchor element for the actions menu.
 * @state {number} page - Current page number for table pagination.
 * @state {number} pagePagination - Current page number for pagination component.
 * @state {number} itemCountPagination - Total number of pages for pagination component.
 * @state {number} rowsPerPage - Number of rows per page in the table.
 * @state {number} timeAlert - Duration for alert messages.
 * @state {any[]} messagesAlerts - List of alert messages.
 * @state {boolean} canEdit - Permission to edit cargos.
 * @state {boolean} canDelete - Permission to delete cargos.
 * @state {boolean} canRestore - Permission to restore cargos.
 * @state {any[]} msgErrors - List of error messages.
 * @state {boolean} open_cargo - State to control the open/close of the create cargo modal.
 * @state {boolean} buttons - State to control the visibility of buttons.
 * @state {boolean} modalDetalleCargo - State to control the open/close of the detail cargo modal.
 * @state {boolean} modalEditarCargo - State to control the open/close of the edit cargo modal.
 * @state {boolean} modalSessionExpired - State to control the open/close of the session expired modal.
 * @state {boolean} alert_snack - State to control the visibility of the snack alert.
 * @state {{msg: string, status: string}} msgSnack - Message and status for the snack alert.
 * @state {any[]} dataTableList - List of cargos to be displayed in the table.
 * @state {any} pagination - Pagination data for the table.
 * 
 * @function handleClick - Handles the click event for opening the actions menu.
 * @function handleClose - Handles the close event for the actions menu.
 * @function getDataCargos - Fetches the list of cargos from the Redux store.
 * @function openDialogDetalle - Opens the detail dialog for a specific cargo.
 * @function openDialogEditar - Opens the edit dialog for a specific cargo.
 * @function desactiveCargo - Deactivates a specific cargo.
 * @function restoreCargo - Restores a specific cargo.
 * @function handleCloseModalCrearCargo - Closes the create cargo modal.
 * @function handleCloseModalDetailCargo - Closes the detail cargo modal.
 * @function handleCloseModalEditarCargo - Closes the edit cargo modal.
 * @function handleChangePage - Handles the change event for table pagination.
 * @function handleChangeRowsPerPage - Handles the change event for rows per page in the table.
 * @function changePaginationPage - Handles the change event for the pagination component.
 * @function checkEdit - Checks if the user has permission to edit cargos.
 * @function checkCanDelete - Checks if the user has permission to delete cargos.
 * @function checkRestore - Checks if the user has permission to restore cargos.
 * @function validateResponse - Validates the response from the Redux store.
 */
export const TableCargos = () => {

  // USE DISPATCH 
  const dispatch = useDispatch();

  // USE SELECTOR 
  const { cargos_list }:any  = useSelector( (state:any) => state.cargos );


  // USE STATE 
  const [openElem, setOpenElem] = React.useState(null);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);


  const [page, setPage] = React.useState(0);
  const [pagePagination, setPagePagination] = React.useState(1);
  const [itemCountPagination, setItemCountPagination] = React.useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState(showNumberRowsTable);


  const [timeAlert, setTimeAlert] = React.useState(timeToClosedToast);
  const [messagesAlerts, setMessagesAlerts] = React.useState<any>([]);


  const [canEdit, setCanEdit]     = React.useState<boolean>(false);
  const [canDelete, setCanDelete]     = React.useState<boolean>(false);
  const [canRestore, setCanRestore]     = React.useState<boolean>(false);


  const [msgErrors, setMsgErrors] = React.useState([]);
  const [open_cargo, setOpenCargo]     = React.useState(false);
  const [buttons, setButtons] = React.useState(false);
  const [modalDetalleCargo, setModalDetalleCargo] = React.useState(false);
  const [modalEditarCargo, setModalEditar] = React.useState(false);

  const [checked, setChecked] = React.useState(true);

  const [modalSessionExpired, setModalSessionExpired] = React.useState<boolean>(false);

  const [alert_snack, setAlertSnack] = React.useState(false);
  const [msgSnack, setMsgSnack] = React.useState({
      msg: '',
      status: 'success'
  });


  const [dataTableList, setDataTable] = React.useState([]);
  const [pagination, setPagination] = React.useState<any>({
      "page": 1,
      "take": 1,
      "itemCount": 1,
      "pageCount": 1,
      "hasPreviousPage": false,
      "hasNextPage": false
  });



   // USE EFFECT 
  React.useEffect(() => {
    checkEdit()
  }, []);

  React.useEffect(() => {
      checkCanDelete()
  }, []);

  React.useEffect(() => {
    checkRestore()
  }, []);
  
  React.useEffect(() => {
    
    if ( cargos_list.data && cargos_list.data.length > 0 ) {

      setDataTable(cargos_list.data);
      setPagination({
        "page": cargos_list.meta.page,
        "take": cargos_list.meta.take,
        "itemCount": cargos_list.meta.itemCount,
        "pageCount": cargos_list.meta.pageCount,
        "hasPreviousPage": cargos_list.meta.hasPreviousPage,
        "hasNextPage": cargos_list.meta.hasNextPage
      });
      setItemCountPagination(cargos_list.meta.pageCount);
      
    }
    

  }, [cargos_list]);


   React.useEffect(() => {
      localStorage.getItem('sctqmheck') === 'true' ? setChecked(true) : setChecked(false);
       getDataCargos();
    }, []);


  // CONST STYLES
  const StyledMenu = styled((props: MenuProps) => (
    <Menu
      elevation={0}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      {...props}
    />
  ))(({ theme }) => ({
    '& .MuiPaper-root': {
      borderRadius: 6,
      marginTop: theme.spacing(1),
      minWidth: 180,
      color:
        theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],
      boxShadow:
        'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
      '& .MuiMenu-list': {
        padding: '7px 0',
      },
      '& .MuiMenuItem-root': {
        '& .MuiSvgIcon-root': {
          fontSize: 18,
          color: theme.palette.text.secondary,
          marginRight: theme.spacing(1.5),
        },
        '&:active': {
          backgroundColor: alpha(
            theme.palette.primary.main,
            theme.palette.action.selectedOpacity,
          ),
        },
      },
    },
  }));
  
  
  // CONST STYLES 
  const Div = styled('div')(({ theme }) => ({
    ...theme.typography.button,
    backgroundColor: 'wheat',
    padding: theme.spacing(8),
    textAlign: 'center'
  }));



  // CONST VALUE
  const open = Boolean(anchorEl);



  // CONST FUNCTIONS 

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setChecked(event.target.checked);
      localStorage.setItem('sctqmheck', event.target.checked.toString());
  };


  const handleClick = (elem:any) => (event:any) => {
    setAnchorEl(event.currentTarget);
    setOpenElem(elem);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOpenElem(null);
  };


  const getDataCargos = async() => {

    
      const response = await dispatch( getCargosStore() );

      await validateResponse(response, ' (Listar Cargos) ');
    
  }


  const openDialogDetalle = async(areaId:string) => {
      
    setAnchorEl(null);
    setOpenElem(null);
    setButtons(false);
       

    const response = await dispatch( storeDetailCargos(areaId) );


    const result = await validateResponse(response, ' (Detalle Cargo) ');

    if( !result ) {
      return false;
    } 


    setModalDetalleCargo(true);

  }

  const openDialogEditar = async(areaId:string) => {
      
       
    // await dispatch( storeDetailAreas(areaId) );

   setAnchorEl(null);
   setOpenElem(null);
   setButtons(false);

   const response = await dispatch( storeDetailCargos(areaId) );


   const result = await validateResponse(response, ' (Detalle Cargo) ');


   if( !result ) {
      return false;
   } 

    setModalEditar(true);


  }


  const desactiveCargo = async(cargoId:string) => {

      setAnchorEl(null);
      setOpenElem(null);
      setButtons(false);

      
      
      const response = await dispatch( storeDesactiveCargo(cargoId) );


      await validateResponse(response, ' (Desactivar Cargo) ');

      
  }


  const restoreCargo = async(cargoId:string) => {

    setAnchorEl(null);
    setOpenElem(null);
    setButtons(false);

    const response = await dispatch( storeActiveCargo(cargoId) );

    await validateResponse(response, ' (Activar Cargo) ');


  }

  

  const handleCloseModalCrearCargo = () => {
        setOpenCargo(false)
  }

  const handleCloseModalDetailCargo = () => {
       setModalDetalleCargo(false);
  }

  
  const handleCloseModalEditarCargo = () => {
       setModalEditar(false);
  }

 


   const handleChangePage = (event: unknown, newPage: number) => {
  
      setPage(newPage);
  
    };
  
  
    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    
          setRowsPerPage(+event.target.value);
    
          setPage(0);
    
    };
  
  
  
    const changePaginationPage = async(event: React.ChangeEvent<unknown>, value: number) => {
            
            setPagePagination(value)
        
            const response =  await dispatch( getCargosByPagination(value) );

            await validateResponse(response, ' (Listar Cargos) ');
            
    };




    const checkEdit = async() => {

      const hasPermissionsEdit = await hasPemissions('modulo.configuracion', 'modulo.cargos', 'edit' );
    
      if ( hasPermissionsEdit ) {
            setCanEdit(true);
      }
   }


  const checkCanDelete = async() => {
      
      const hasPermissionsDelete = await hasPemissions('modulo.configuracion', 'modulo.cargos', 'delete' );
      
      if ( hasPermissionsDelete ) {
            setCanDelete(true);
      }

  }


  const checkRestore = async() => {
      
      const hasPermissionsRestore = await hasPemissions('modulo.configuracion', 'modulo.cargos', 'restore' );
        
      if ( hasPermissionsRestore ) {
            setCanRestore(true);
      }

  }



  

  const validateResponse = async(response:any, textActionConcat: string) => {
  
  
        if (  response.code == 401  ) {
          await localStorage.removeItem('lastSelectedQypht');
          await closeSessionGlobal();
          setModalSessionExpired(true);
          return false;
        }

      
        // 403 NO TIENE PERMISSO PARA REALIZAR ESA ACCION 
        if (!response.ok && response.code == 403 ) { 

          const errors:any = {
            msg: response.errors[0]+' '+textActionConcat,
            status: 'error'
          };
        
          await setMsgSnack(errors);
          await setAlertSnack(true);

        
          return false;

        }

        
        // 400 ERROR EN DATOS ENVIADOS MAYORMENTE PROPIEDAD INVALIDA O MAL FORMATEADO 
        if (!response.ok && response.code == 400 ) { 

            const errors:any = {
              msg: response.errors[0]+' '+textActionConcat,
              status: 'error'
            };
          
            await setMsgSnack(errors);
            await setAlertSnack(true);

          
            return false;

        }

        return true;
      
  }


  
  return (

    <>

        <ModalSessionExpired  open={modalSessionExpired}  />
        <MultipleSnackbars messagesList={messagesAlerts} position={"bottom-left"} autoClose={timeAlert} />
        <SnackAlert open={alert_snack} onclick={setAlertSnack} text={msgSnack.msg} severity={msgSnack.status}/>

        <CreateDialogCargo open={open_cargo} handleCloseModalCrearCargo={handleCloseModalCrearCargo} buttons={buttons}/>
        <DetailDialogCargo open={modalDetalleCargo} handleCloseModalDetailCargo={handleCloseModalDetailCargo} />
        <EditDialogCargo  open={modalEditarCargo} handleCloseModalEditarCargo={handleCloseModalEditarCargo} />
       

       {
            
            dataTableList.length > 0
                
              ?
              <>
              <Card
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 3,
                  borderRadius: 2,
                  boxShadow: 3,
                  bgcolor: 'background.paper',
                  marginTop: paddingCardsTables
                }}
              >
                <TableContainer component={Paper}>
                  <FormGroup>
                      <FormControlLabel sx={{marginLeft: 2}} control={<Switch checked={checked} onChange={handleChange} />} label="Mostrar cargos (inactivos)" />
                  </FormGroup>
                  <Table sx={{ minWidth: 700 }} aria-label="customized table">
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>#</StyledTableCell>
                        <StyledTableCell >Cargo</StyledTableCell>
                        <StyledTableCell >Descripcion</StyledTableCell>
                        <StyledTableCell >Area</StyledTableCell>
                        <StyledTableCell >Estado</StyledTableCell>
                        <StyledTableCell >Acciones</StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {
                      dataTableList
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((cargo:any, index:any) => (
                        <StyledTableRow key={index+1}>
                          <StyledTableCell component="th" scope="row">
                            {index+1}
                          </StyledTableCell>
                          <StyledTableCell component="th" scope="row">
                            {usarFuncionesComposicion(corregirTexto, firstLetterUpperCase, cargo.name)}
                          </StyledTableCell>
                          <StyledTableCell >
                            {usarFuncionesComposicion(corregirTexto, firstLetterUpperCase, cargo.description)}
                          </StyledTableCell>
                          <StyledTableCell >
                            {usarFuncionesComposicion(corregirTexto, firstLetterUpperCase, cargo.area.name)}
                          </StyledTableCell>
                          <StyledTableCell >
                            {
                              cargo.status ? <Chip size='small' label="Activo" sx={{ color: '#5DCC0A', background: '#E4F6D6', fontWeight: 700 }} /> : <Chip size='small' label="Inactivo" sx={{ color: '#FF4C51', background: '#FFE2E3', fontWeight: 700 }} />
                            }
                          </StyledTableCell>
                          <StyledTableCell  >
                                
                          
                          {
                          
                              <div> 
                                  <Button
                                    id={`demo-customized-button-${index+1}`}
                                    aria-controls={open ? `demo-customized-menu-${index+1}` : undefined}
                                    aria-haspopup="true"
                                    aria-expanded={open ? 'true' : undefined}
                                    variant="text"
                                    disableElevation
                                    onClick={handleClick(cargo)}
                                    endIcon={<KeyboardArrowDownIcon />}
                                  >
                                    Acciones
                                  </Button>
                                  <StyledMenu
                                    id={`demo-customized-menu-${index+1}`}
                                    MenuListProps={{
                                      'aria-labelledby': `demo-customized-button-${index+1}`,
                                    }}
                                    anchorEl={anchorEl}
                                    open={openElem === cargo}
                                    onClose={handleClose}
                                  >
                                    
                                    {
                                      (cargo.status) 
                                        ? <div>
                                          <MenuItem onClick={ () => openDialogDetalle(cargo.id) }  disableRipple>
                                          <VisibilityIcon />
                                            Detalle 
                                          </MenuItem>
                                          {
                                             canEdit && 
                                              <>
                                                <Divider sx={{ my: 0.5 }} />
                                                <MenuItem onClick={ () => openDialogEditar(cargo.id) }  disableRipple>
                                                  <EditIcon />
                                                    Editar
                                                </MenuItem>
                                              </>
                                          }
                                        </div>
                                        :
                                        ''
                                    }
                                    
                                    {
                                      (cargo.status) 
                                      ?
                                        <div>
                                           {
                                              canDelete && 
                                              <>
                                                <Divider sx={{ my: 0.5 }} />
                                                <MenuItem onClick={ () => desactiveCargo(cargo.id) } disableRipple>
                                                  <DeleteIcon />
                                                  Desactivar
                                                </MenuItem>
                                              </>
                                           }
                                        </div>
                                      :
                                      <div>
                                         {
                                            canRestore && 
                                            <>
                                               <Divider sx={{ my: 0.5 }} />
                                              <MenuItem onClick={ () => restoreCargo(cargo.id) } disableRipple>
                                                <DeleteIcon />
                                                Activar
                                              </MenuItem>
                                            </>
                                         }
                                      </div>
                                      
                                    }
                                    </StyledMenu>
                                </div>
                      
                          }
          
          
          
          
                          </StyledTableCell>
                        </StyledTableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
              <TablePagination
                rowsPerPageOptions={[5, 10]}
                labelRowsPerPage={'Filas por pagina'}
                labelDisplayedRows={({ from, to, count }) => ` ${from}-${to} de  ${count} `}
                component="div"
                count={pagination.itemCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={ handleChangePage }
                onRowsPerPageChange={ handleChangeRowsPerPage }
              />

              <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      marginTop: 2,
                      marginBottom: 2
                    }}
                  >
                  <Pagination sx={{ justifyContent: 'center' }} color="primary" count={itemCountPagination} page={pagePagination} onChange={ changePaginationPage} showFirstButton showLastButton />
              </Box>
              </>
              :
              <Div>{"No hay cargos disponibles..."}</Div>
       }


       
       
    </>
    
  );
}


